import type { GencowCtx } from "./context.js";
import type { WorkflowDocumentServicesCtx } from "./document-types.js";
import type { InferArgs } from "./v.js";

export type WorkflowStatus = "pending" | "running" | "completed" | "failed";
export type WorkflowDuration = number | string;
export type WorkflowDerivedStatus = WorkflowStatus | "queued" | "waiting" | "sleeping";

export function deriveWorkflowStatus(
  status: WorkflowStatus,
  currentStep: string | null | undefined,
): WorkflowDerivedStatus {
  if (status !== "pending") {
    return status;
  }
  if (currentStep?.startsWith("sleep#")) {
    return "sleeping";
  }
  if (currentStep?.startsWith("wait:")) {
    return "waiting";
  }
  return "queued";
}

export interface WorkflowSummary {
  id: string;
  name: string;
  status: WorkflowStatus;
  derivedStatus: WorkflowDerivedStatus;
  currentStep: string | null;
  error: string | null;
  errorCode: string | null;
  retryCount: number;
  maxRetries: number;
  maxDurationMs: number;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface WorkflowStepSnapshot {
  name: string;
  status: WorkflowStatus;
  output: unknown;
  error: string | null;
  startedAt: string | null;
  updatedAt: string;
  completedAt: string | null;
}

export interface WorkflowSnapshot extends WorkflowSummary {
  args: unknown;
  result: unknown;
  steps: WorkflowStepSnapshot[];
  /** Opaque realtime channel for exact-id workflow updates. */
  realtimeKey: string;
}

export interface WorkflowListArgs {
  limit?: number;
  status?: WorkflowDerivedStatus;
}

export interface WorkflowStartResult {
  id: string;
  name: string;
  status: "pending";
  scheduledJobId: string;
}

export interface WorkflowSignalResult {
  ok: boolean;
  workflowId: string;
  event: string;
  scheduledJobId: string | null;
}

export interface WorkflowResumePayload {
  workflowId: string;
}

export interface WorkflowCtx extends Omit<GencowCtx, "services"> {
  workflowId: string;
  workflowName: string;
  runId?: string;
  attempt?: number;
  stepAttempt?: number;
  services: GencowCtx["services"] & WorkflowDocumentServicesCtx;
  step<TResult>(name: string, run: () => Promise<TResult>): Promise<TResult>;
  sleep(duration: WorkflowDuration): Promise<void>;
  waitForEvent<TPayload = unknown>(name: string, timeout?: WorkflowDuration): Promise<TPayload>;
  parallel<TTasks extends ReadonlyArray<() => Promise<any>>>(
    tasks: TTasks,
  ): Promise<{ [K in keyof TTasks]: Awaited<ReturnType<TTasks[K]>> }>;
}

export type WorkflowHandler<TArgs, TReturn> = (wf: WorkflowCtx, args: TArgs) => Promise<TReturn>;

export interface WorkflowOptions<TSchema = any, TReturn = any> {
  args?: TSchema;
  public?: boolean;
  version?: string;
  maxDuration?: WorkflowDuration;
  maxActiveDuration?: WorkflowDuration;
  lifecycleTimeout?: WorkflowDuration;
  concurrency?: number;
  retries?: number;
  handler: WorkflowHandler<InferArgs<TSchema>, TReturn>;
}

export interface WorkflowDef<TSchema = any, TReturn = any> {
  name: string;
  argsSchema?: TSchema;
  isPublic: boolean;
  version?: string;
  maxDurationMs: number;
  maxActiveDurationMs: number;
  lifecycleTimeoutMs: number | null;
  concurrency: number | null;
  maxRetries: number;
  handler: WorkflowHandler<InferArgs<TSchema>, TReturn>;
}
